/** P-06 — patient prescription list (filter by family member). */

import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { Link } from "react-router-dom";

import { listFamily, listPrescriptions } from "../../api/patient";
import { Select } from "../../components/ui/Select";

export function ScheduleBadge({ cls }: { cls: string }) {
  const tone =
    cls === "X" ? "bg-red-50 text-red-700" : cls === "H1" ? "bg-amber-50 text-amber-700" : cls === "H" ? "bg-yellow-50 text-yellow-700" : "bg-gray-100 text-gray-500";
  // A badge never breaks inside its label ("Sch / H") when its row is tight (RESP-11).
  return <span className={`shrink-0 whitespace-nowrap rounded-full px-2 py-0.5 text-xs font-medium ${tone}`}>{cls === "none" ? "OTC" : `Sch ${cls}`}</span>;
}

export function StatusBadge({ status }: { status: string }) {
  const map: Record<string, { tone: string; label: string }> = {
    active: { tone: "bg-emerald-50 text-emerald-700", label: "Active" },
    expired: { tone: "bg-red-50 text-red-700", label: "Expired" },
    consumed: { tone: "bg-gray-100 text-gray-500", label: "Used" },
  };
  const { tone, label } = map[status] ?? { tone: "bg-gray-100 text-gray-500", label: status };
  return <span className={`shrink-0 whitespace-nowrap rounded-full px-2 py-0.5 text-xs font-medium ${tone}`}>{label}</span>;
}

export function Prescriptions() {
  const [familyId, setFamilyId] = useState("");
  const { data: family = [] } = useQuery({ queryKey: ["family"], queryFn: listFamily });
  const { data } = useQuery({
    queryKey: ["prescriptions", familyId],
    queryFn: () => listPrescriptions(familyId ? { family_member_id: familyId } : {}),
  });
  const familyName = (id: string | null) => family.find((f) => f.id === id)?.full_name;

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="text-lg font-semibold">Prescriptions</h2>
          <p className="text-sm text-gray-500">Your and your family's prescriptions.</p>
        </div>
        {/* A long family member's name made this filter wider than a phone (428px at 360): the
            field shrinks to the screen and cuts the name short (RESP-36). */}
        <label className="flex min-w-0 max-w-full items-center gap-2 text-sm">
          <span className="text-gray-500">For</span>
          <Select
            value={familyId}
            options={[
              { value: "", label: "Everyone" },
              { value: "self", label: "— self —", disabled: true },
              ...family.map((f) => ({ value: f.id, label: f.full_name })),
            ]}
            onChange={setFamilyId}
          />
        </label>
      </div>

      <div className="space-y-3">
        {(data?.data ?? []).map((rx) => (
          <Link
            key={rx.id}
            to={`/patient/prescriptions/${rx.id}`}
            className="block rounded-xl border bg-white p-4 shadow-xs transition hover:border-brand"
          >
            <div className="flex items-center justify-between gap-3">
              {/* "for <family member>" wraps under the badge rather than squeezing it (RESP-11). */}
              <div className="flex min-w-0 flex-wrap items-center gap-x-2 gap-y-1">
                <ScheduleBadge cls={rx.schedule_class} />
                <span className="font-medium">{rx.line_count} medicine{rx.line_count === 1 ? "" : "s"}</span>
                {rx.for_family_member_id && (
                  <span className="text-sm text-gray-500">for {familyName(rx.for_family_member_id) ?? "family member"}</span>
                )}
              </div>
              <StatusBadge status={rx.status} />
            </div>
            <div className="mt-1 text-xs text-gray-400">
              Issued {new Date(rx.issued_at).toLocaleDateString()} · valid till {new Date(rx.expires_at).toLocaleDateString()}
            </div>
            {rx.notes && <div className="mt-1 truncate text-sm text-gray-600">{rx.notes}</div>}
          </Link>
        ))}
        {data && data.data.length === 0 && <p className="text-sm text-gray-400">No prescriptions found.</p>}
      </div>
    </div>
  );
}
